import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import useGameStore from '@/store/gameStore';
import websocketService from '@/services/websocket';
import GameBoard from './game/GameBoard';
import GameHeader from './game/GameHeader';
import PlayerList from './game/PlayerList';
import GameControls from './game/GameControls';
import LoadingSpinner from './LoadingSpinner';

function GameRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { gameState, isConnected, error } = useGameStore();

  useEffect(() => {
    if (!roomId) {
      navigate('/lobby');
      return;
    }

    // Connect to WebSocket
    websocketService.connect(roomId);

    // Cleanup on unmount
    return () => {
      websocketService.disconnect();
      useGameStore.getState().reset();
    };
  }, [roomId, navigate]);

  if (!isConnected && !error) {
    return <LoadingSpinner fullScreen message="Connecting to game room..." />;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card max-w-md w-full text-center">
          <div className="text-danger-500 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Connection Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/lobby')}
            className="btn-primary"
            data-testid="btn-return-lobby"
          >
            Return to Lobby
          </button>
        </div>
      </div>
    );
  }

  if (!gameState) {
    return <LoadingSpinner fullScreen message="Loading game state..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100">
      <div className="container mx-auto px-4 py-6">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-6"
        >
          {/* Header - Full Width */}
          <div className="lg:col-span-12">
            <GameHeader />
          </div>

          {/* Player List - Left Sidebar */}
          <div className="lg:col-span-3">
            <PlayerList />
          </div>

          {/* Game Board - Main Content */}
          <div className="lg:col-span-6">
            <AnimatePresence mode="wait">
              <GameBoard key={gameState.phase} />
            </AnimatePresence>
          </div>

          {/* Game Controls - Right Sidebar */}
          <div className="lg:col-span-3">
            <GameControls />
          </div>
        </motion.div>
      </div>

      {/* Connection Status Indicator */}
      <div className="fixed bottom-4 left-4">
        <AnimatePresence>
          {!isConnected && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-warning-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center gap-2"
            >
              <span className="animate-pulse">●</span>
              Reconnecting...
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default GameRoom;
